import { gql, TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { createContext, ReactNode } from 'react';

import { useAuth } from 'src/user';

export type ExtranetFeatures = {
  hasVideoConference: boolean;
  isVideoConferenceV1: boolean;
  hasElearning: boolean;
  hasPositioningTest: boolean;
  hasLiveQuiz: boolean;
  hasAcademyDescription: boolean;
  hasVideocallDevelopmentFeatures: boolean;
  scormOpenInIframe: boolean;
};

type ExtranetFeaturesProviderProps = {
  children: ReactNode;
};

type ExtranetFeaturesQuery = {
  extranetFeatures: ExtranetFeatures;
};

type ExtranetFeaturesQueryVariables = {
  userCode: string;
};

export const EXTRANET_FEATURES_QUERY: TypedDocumentNode<ExtranetFeaturesQuery, ExtranetFeaturesQueryVariables> = gql`
  query ($userCode: String!) {
    extranetFeatures(userCode: $userCode) {
      hasVideoConference
      isVideoConferenceV1
      hasElearning
      hasPositioningTest
      hasLiveQuiz
      hasAcademyDescription
      hasVideocallDevelopmentFeatures
      scormOpenInIframe
    }
  }
`;

export const ExtranetFeaturesContext = createContext<ExtranetFeatures | undefined>(undefined);

/**
 * Load and provide the currently enabled features of the extranet.
 * Use the `useExtranetFeatures` hook to get the list of those features and their
 * activation state for the current user.
 */
export function ExtranetFeaturesProvider(props: ExtranetFeaturesProviderProps) {
  const { auth } = useAuth();
  const userCode = auth?.userCode;
  const { data, loading, error } = useQuery(EXTRANET_FEATURES_QUERY, {
    variables: { userCode: userCode! },
    skip: !userCode,
  });

  if (error) {
    throw new Error(`Failed fetching extranet features: ${error}`);
  }

  if (!data || loading) {
    return null;
  }

  return <ExtranetFeaturesContext.Provider value={data.extranetFeatures}>{props.children}</ExtranetFeaturesContext.Provider>;
}
